import { useCallback, useEffect, useMemo, useState } from "react";
import { getNodeOptions } from "../services/nodeOptions";
import { useOptionsClientId } from "./useOptionsClientId";

const DEPENDENCY = /\{(\w+)\}/g;

/** The fields a request depends on: `depends_on` plus any `{field}` used in the query templates. */
export const requestDependencyFields = (controlOptions = {}) => {
  const fields = new Set(Array.isArray(controlOptions?.depends_on) ? controlOptions.depends_on : []);

  Object.values(controlOptions?.request?.query ?? {}).forEach((template) => {
    if (typeof template !== "string") return;
    for (const match of template.matchAll(DEPENDENCY)) fields.add(match[1]);
  });

  fields.delete("source");
  return [...fields];
};

/**
 * Load a node option source using the catalog's request metadata.
 *
 * Missing-scope failures come back as HTTP 200 with `success: false`, so they are kept as
 * actionable state (`errorDetail`) rather than a generic transport error.
 */
export const useNodeOptions = ({ controlOptions = null, source = null, data = {}, enabled = true } = {}) => {
  const clientId = useOptionsClientId();

  const [options, setOptions] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [nonce, setNonce] = useState(0);

  // Refetch when the values the request depends on change (e.g. `clientKey`), not on every
  // keystroke elsewhere in the node.
  const dependencyKey = useMemo(() => {
    const fields = requestDependencyFields(controlOptions ?? {});
    return JSON.stringify(fields.map((field) => [field, data?.[field] ?? null]));
  }, [controlOptions, data]);

  const load = useCallback(
    async (cursor = null, append = false) => {
      if (!enabled || !source) {
        setOptions([]);
        setNextCursor(null);
        return;
      }

      setLoading(true);

      const result = await getNodeOptions({ controlOptions, source, data, clientId, cursor });

      setLoading(false);

      if (result?.success === false) {
        if (!append) setOptions([]);
        setNextCursor(null);
        setError(result.message || "Could not load the options.");
        setErrorDetail(result);
        return;
      }

      setError(null);
      setErrorDetail(null);
      setOptions((current) => (append ? [...current, ...(result.options ?? [])] : result.options ?? []));
      setNextCursor(result.next_cursor ?? null);
    },
    // `data` is read at call time; `dependencyKey` captures the values that actually matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, controlOptions, source, clientId, dependencyKey]
  );

  useEffect(() => {
    load(null, false);
  }, [load, nonce]);

  // Saving or deleting a client API key invalidates the key pickers.
  useEffect(() => {
    const onChange = () => setNonce((value) => value + 1);

    window.addEventListener("client-api-keys:changed", onChange);
    return () => window.removeEventListener("client-api-keys:changed", onChange);
  }, []);

  return {
    options,
    nextCursor,
    loading,
    error,
    errorDetail,
    clientId,
    loadMore: () => load(nextCursor, true),
    reload: () => setNonce((value) => value + 1),
  };
};

export default useNodeOptions;
